import { SUBSCRIPTION_PACKAGES } from 'constants/ai'
import { PAGES } from 'constants/routes'

// Pricing period types
export enum PricingPeriodType {
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
  CANCELED = 'canceled',
  TRIAL = 'trial',
}

// Action type enum for different button actions
export enum ActionType {
  SUBSCRIBE = 'SUBSCRIBE',
  SWITCH = 'SWITCH',
  REACTIVATE = 'REACTIVATE',
  CANCEL = 'CANCEL',
}

// Button properties interface
export interface ButtonProps {
  buttonText: string
  actionType: ActionType
  disabled: boolean
  isLoading: boolean
}

/**
 * Determines the button properties for pricing cards based on plan and user state
 * @param planId - The ID of the plan
 * @param pricingPeriod - The pricing period
 * @param isLoading - Whether the data is loading
 * @param isLoggedIn - Whether the user is logged in
 * @param isSubscriptionActive - Whether the user has an active subscription
 * @param isSubscriptionExpired - Whether the user's subscription has expired
 * @param isSubscriptionCanceled - Whether the user's subscription has been canceled
 * @param isSubscriptionInTrial - Whether the user's subscription is in trial
 * @param subscriptionType - The type of subscription the user has
 * @param isAdFreeSubscription - Whether the user has an active ad-free subscription
 * @param planType - The type of plan (premium or ad-free)
 */
export const getButtonProps = (
  pricingPeriod: PricingPeriodType,
  isLoading: boolean,
  isLoggedIn: boolean,
  isSubscriptionActive: boolean,
  isSubscriptionExpired: boolean,
  isSubscriptionCanceled: boolean,
  isSubscriptionInTrial: boolean,
  isAdFreeSubscription: boolean,
  planType: 'premium' | 'ad-free',
  subscriptionType?: string,
  scheduledCancelEffectiveAt?: string | null
): ButtonProps => {
  const isMonthlyPricing = pricingPeriod === PricingPeriodType.MONTHLY
  const isYearlyPricing = pricingPeriod === PricingPeriodType.YEARLY

  // Determine if user has monthly, yearly subscription
  const hasMonthlySubscription = isSubscriptionActive && subscriptionType === PricingPeriodType.MONTHLY
  const hasYearlySubscription = isSubscriptionActive && subscriptionType === PricingPeriodType.YEARLY
  const hasAdFreeSubscription = isSubscriptionActive && isAdFreeSubscription

  // If data is loading, show loading state
  if (isLoading) {
    return {
      buttonText: 'Loading...',
      actionType: ActionType.SUBSCRIBE,
      disabled: true,
      isLoading: true,
    }
  }

  // Default values for button text and action
  let buttonText = isLoggedIn ? '🚀Subscribe Now' : '🚀Login and Subscribe'

  let actionType = ActionType.SUBSCRIBE
  // Disable only when in trial AND not scheduled to cancel
  const isScheduledToCancel = Boolean(scheduledCancelEffectiveAt)
  let disabled = isSubscriptionInTrial && !isSubscriptionExpired && !isScheduledToCancel

  if (!isLoggedIn) {
    // Scenario 1: User is not logged in
    // Default values are already set
  } else if (isSubscriptionActive && !isScheduledToCancel) {
    // Scenario 2: User is logged in and has an active subscription and not scheduled to cancel
    if (hasAdFreeSubscription) {
      // User has ad-free subscription
      if (planType === 'ad-free') {
        if (isMonthlyPricing && hasMonthlySubscription) {
          buttonText = isSubscriptionInTrial ? 'Current plan (Trial)' : 'Current plan'
          disabled = true
        } else if (isYearlyPricing && hasYearlySubscription) {
          buttonText = isSubscriptionInTrial ? 'Current plan (Trial)' : 'Current plan'
          disabled = true
        } else {
          buttonText = isMonthlyPricing ? 'Switch to Monthly Ad-Free Plan' : 'Switch to Yearly Ad-Free Plan'
          actionType = ActionType.SWITCH
          disabled = hasYearlySubscription || (isSubscriptionInTrial && !isSubscriptionExpired)
        }
      } else {
        // Premium card for ad-free subscriber
        buttonText = 'Switch to Premium Plan'
        actionType = ActionType.SWITCH
      }
    } else if (hasMonthlySubscription || hasYearlySubscription) {
      // User has premium subscription
      if (planType === 'premium') {
        if ((hasMonthlySubscription && isMonthlyPricing) || (hasYearlySubscription && isYearlyPricing)) {
          buttonText = isSubscriptionInTrial ? 'Current plan (Trial)' : 'Current plan'
          disabled = true
        } else {
          buttonText = hasMonthlySubscription ? 'Switch to Yearly Plan' : 'Switch to Monthly Plan'
          actionType = ActionType.SWITCH
          disabled = hasYearlySubscription || (isSubscriptionInTrial && !isSubscriptionExpired)
        }
      } else {
        // Ad-free card for premium subscriber
        buttonText = 'Switch to Ad-Free Plan'
        actionType = ActionType.SWITCH
      }
    }
  } else if (isSubscriptionExpired || isSubscriptionCanceled || isScheduledToCancel) {
    // Scenario 3: User is logged in and has an expired, scheduled to cancel or canceled subscription
    switch (planType) {
      case 'premium':
        actionType = ActionType.REACTIVATE
        buttonText = isMonthlyPricing ? 'Re-activate Monthly Premium Plan' : 'Re-activate Yearly Premium Plan'
        break
      case 'ad-free':
        buttonText = isMonthlyPricing ? 'Re-activate Monthly Ad-Free Plan' : 'Re-activate Yearly Ad-Free Plan'
        actionType = ActionType.REACTIVATE
        break
    }
  }

  return {
    buttonText,
    actionType,
    disabled,
    isLoading: false,
  }
}

/**
 * Handles pricing action when buttons are clicked
 * @param action - The action to perform
 * @param pricingPeriod - The pricing period
 * @param isLoading - Whether the data is loading
 * @param isLoggedIn - Whether the user is logged in
 * @param handleCheckout - Function to open the Paddle checkout modal with metadata
 * @returns The path to navigate to, if any
 */
export const handleAction = (
  action: ActionType,
  pricingPeriod: PricingPeriodType,
  isLoading: boolean,
  isLoggedIn: boolean,
  planType: (typeof SUBSCRIPTION_PACKAGES)[keyof typeof SUBSCRIPTION_PACKAGES],
  handleCheckout: (metadata: {
    action: ActionType
    pricingPeriod: PricingPeriodType
    planType?: (typeof SUBSCRIPTION_PACKAGES)[keyof typeof SUBSCRIPTION_PACKAGES]
  }) => void
): void | string => {
  // Prevent actions while loading
  if (isLoading) return

  // Check if user is logged in
  if (!isLoggedIn) {
    // Navigate to login page if not logged in, with a return URL to come back to pricing
    // Use a complete URL including protocol and host, not just a path
    return `${PAGES['/login/']}?destination=${encodeURIComponent(
      typeof window !== 'undefined' ? window.location.href : PAGES['/pricing/']
    )}`
  }

  // Open paddle checkout modal for the selected plan
  // This will open the Paddle checkout modal with the selected plan
  handleCheckout({ action, pricingPeriod, planType })
}
